import React, { useMemo, useRef } from "react"
import { cva } from "class-variance-authority"
import cn from "../../../utils/classes"

export interface LinkProps {
  destination: string
  text: string
  download?: string
  style?: "default" | "light-blue" | "black" | "sky-blue"
  underlined?: boolean
  styleOverrides?: string
  linkRef?: React.RefObject<HTMLAnchorElement> // renamed to avoid shadowing
}

const Link: React.FC<LinkProps> = ({
  destination,
  text,
  download,
  style,
  underlined = false,
  styleOverrides,
  linkRef, // renamed to avoid shadowing
  ...props
}) => {
  const variants = cva(["leading-6"], {
    variants: {
      variant: {
        default: "text-font-blue hover:text-link-blue-hover",
        "light-blue": "text-link-blue hover:text-link-blue-hover",
        black: "text-black hover:text-font-blue-hover",
        "sky-blue": "text-sky-600 hover:text-font-blue-hover",
      },
      underlined: {
        true: "underline",
        false: "no-underline",
      },
    },
  })

  const className = useMemo(() => {
    return cn(variants({ variant: style, underlined }), styleOverrides)
  }, [style, underlined, styleOverrides, variants])

  const backupRef = useRef<HTMLAnchorElement>(null)
  const usedRef = linkRef || backupRef

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault()
      usedRef.current?.click()
    }
  }

  return (
    <a
      ref={usedRef}
      href={`${destination}`}
      download={download}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      onKeyDown={handleKeyDown}
      {...props}
    >
      {text}
    </a>
  )
}

export default Link
